import { z } from "zod";
import { ISODateSchema, ULIDSchema } from "./primitives.js";
import { UserIdSchema } from "./user.js";
// 64 lowercase hex chars (sha256 of the raw 256-bit token).
const Sha256HexSchema = z.string().regex(/^[a-f0-9]{64}$/);
// 64 lowercase hex chars (raw 32-byte token; 256 bits = 64 hex chars).
// Spec says 256-bit token; we generate 32 random bytes → 64 hex chars in the raw form.
const RawTokenSchema = z.string().regex(/^[a-f0-9]{64}$/);
// Password complexity mirrors the Cognito User Pool PasswordPolicy in template.yaml.
// Keeping it in shared/ means backend and frontend agree before the request hits Cognito.
export const PASSWORD_MIN_LENGTH = 14;
export const PasswordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Must be at least ${PASSWORD_MIN_LENGTH} characters`)
    .max(256, "Must be 256 characters or fewer")
    .regex(/[a-z]/, "Must contain a lowercase letter")
    .regex(/[A-Z]/, "Must contain an uppercase letter")
    .regex(/\d/, "Must contain a digit")
    .regex(/[^A-Za-z0-9]/, "Must contain a symbol");
export const InviteRecordSchema = z
    .object({
    schemaVersion: z.literal(1),
    inviteId: ULIDSchema("inv"),
    tokenHash: Sha256HexSchema,
    email: z.string().email(),
    householdId: ULIDSchema("hh"),
    // invitedBy / consumedBy = raw Cognito subs. See "Resolved scope decisions" #9.
    invitedBy: UserIdSchema,
    createdAt: ISODateSchema,
    expiresAt: ISODateSchema,
    consumedAt: ISODateSchema.nullable(),
    consumedBy: UserIdSchema.nullable().optional(),
    attemptsRemaining: z.number().int().min(0).max(7).optional(),
})
    .passthrough();
export const CreateInviteRequestSchema = z
    .object({
    email: z.string().email(),
})
    .strict();
export const CreateInviteResponseSchema = z.object({
    inviteId: ULIDSchema("inv"),
    email: z.string().email(),
    expiresAt: ISODateSchema,
});
export const AcceptInviteRequestSchema = z
    .object({
    token: RawTokenSchema,
    password: PasswordSchema,
    displayName: z.string().min(1).max(80),
})
    .strict();
// Invite preview — read-only lookup used by the AcceptInvite page on mount
// so the frontend can show "active" / "expired" / "consumed" before the user
// fills the form. Does NOT consume or decrement attemptsRemaining.
export const InvitePreviewRequestSchema = z
    .object({
    token: RawTokenSchema,
})
    .strict();
export const InvitePreviewResponseSchema = z.discriminatedUnion("status", [
    z.object({
        status: z.literal("active"),
        email: z.string().email(),
        householdName: z.string(),
        expiresAt: ISODateSchema,
    }),
    z.object({ status: z.literal("expired") }),
    z.object({ status: z.literal("consumed") }),
    z.object({ status: z.literal("not_found") }),
]);
export const AcceptInviteResponseSchema = z.object({
    userId: UserIdSchema,
    householdId: ULIDSchema("hh"),
    tokens: z.object({
        accessToken: z.string().min(10),
        idToken: z.string().min(10),
        refreshToken: z.string().min(10),
        expiresIn: z.number().int().positive(),
    }),
});
export const InviteListItemSchema = z.object({
    inviteId: ULIDSchema("inv"),
    email: z.string().email(),
    createdAt: ISODateSchema,
    expiresAt: ISODateSchema,
});
//# sourceMappingURL=invite.js.map